import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import EntryOptionType from 'enums/EntryOptionType';

import { RoomPresenceService } from '../services/room/room-presence.service';
import { ConnectedRoomCounts, socialAppSelectors } from '../state/app';
import { MatDialog } from '@angular/material/dialog';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';

@Component({
  selector: 'app-connected-rooms-select-option',
  templateUrl: './connected-rooms-select-option.component.html',
  styleUrls: ['./connected-rooms-select-option.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectedRoomsSelectOptionComponent extends Loggable implements OnInit, OnDestroy, OnChanges {
  private store = inject(Store);
  private roomPresenceService = inject(RoomPresenceService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  public readonly initialSelectedRoomId = input<string>();

  public connectedRoomSet: Social[] = [];
  private connectedRoomCounts: ConnectedRoomCounts;
  public selectedRoomId?: string;
  private isPresentInRoom = false;
  private entryOption?: EntryOptionType;
  private roomId?: string;

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected newVisualIndicators = false;

  public constructor() {
    super();
    this.setDisplayName('ConnectedRoomsSelectOption');
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.getCurrentSocialId)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((roomId) => {
        this.roomId = roomId;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((set) => {
        this.connectedRoomSet = set.socials ?? [];
        this.entryOption = set.entryOption;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomCounts)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((updatedCounts: ConnectedRoomCounts) => {
        this.connectedRoomCounts = updatedCounts;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentInRoom) => {
        this.isPresentInRoom = isPresentInRoom;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => {
        this.newVisualIndicators = newVisualIndicators;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes.initialSelectedRoomId &&
      !changes.initialSelectedRoomId.previousValue &&
      changes.initialSelectedRoomId.currentValue
    ) {
      this.selectedRoomId = this.initialSelectedRoomId();
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public onRoomChange({ socialId }: Social): void {
    this.logUserAction('onRoomChange', 'select', { selectedRoomId: socialId, previousRoomId: this.roomId });
    if (!socialId || socialId === this.roomId) {
      return;
    }

    if (!this.isPresentInRoom) {
      this.roomPresenceService.changeInitialRoom(socialId);
      return;
    }

    const data = {
      modalTitle: 'Change Room',
      text: 'Are you sure you want to leave this room?',
      primaryButtonText: 'Continue',
      tertiaryButtonText: 'Cancel',
    };

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      data,
    });

    dialogRef.afterClosed().subscribe((confirm: 'primary' | 'tertiary' | undefined) => {
      if (confirm === 'primary') {
        this.roomPresenceService.changeRoom(socialId);
        this.changeDetectorRef.markForCheck();
        return;
      }
      this.selectedRoomId = this.initialSelectedRoomId();
      this.changeDetectorRef.markForCheck();
    });
  }

  public isConnectedRoomFull(room: Social): boolean {
    const roomCount = this.getRoomCount() ?? 0;
    const maxParticipants = room.maxParticipants ?? 0;
    return roomCount > maxParticipants - 1;
  }

  public getRoomCount(room?: Social): number | undefined {
    if (!this.connectedRoomCounts) {
      return;
    }
    const { socialId } = room ?? {};
    if (!socialId) {
      return 0;
    }
    return this.connectedRoomCounts[socialId] ?? 0;
  }

  public get dropdownDisabled(): boolean {
    if (this.isPresentInRoom) {
      return false;
    }
    return this.entryOption === EntryOptionType.CUSTOM_RULES || this.entryOption === EntryOptionType.STARTING_POINT;
  }

  public get isConnected(): boolean {
    return (this.connectedRoomSet ?? []).length > 1;
  }

  protected get selectedRoomCount() {
    if (!this.connectedRoomCounts) {
      return 0;
    }

    if (!this.selectedRoomId) {
      return 0;
    }

    return this.connectedRoomCounts[this.selectedRoomId];
  }
}
